import Grid from '@mui/material/Grid';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import DateInput from '/@/components/forms/date-input';
import useUnitContext from '/@/core/context/unit';
import settings from '/@/core/settings';
import { useFieldById } from '/@/hooks/fields';
import { useRecommendationById } from '/@/hooks/my-recommendations/useFilterMyRecommendations';
import { _ } from '/@/locale';
import {
    setBtnDisabled,
    setDensity,
    setEnvironment,
    setHumidity,
    setPlantingDate,
} from '/@/store/crop-monitoring/actions';
import { getBtnDisabled, getEnvironment, getPlantingDate, getRecommendation } from '/@/store/crop-monitoring/selectors';
import { formatDate } from '/@/utils';
import { assignYearsToDateRange } from '/@/utils/date';
import useIsMobile from '/@/utils/hooks/is-mobile';
import Input from '/@/v3/components/forms/input';
import Title from '/@/v3/components/title';
import Alert from '../../bayer/alert';

const EnvironmentStepper = ({ selected: fieldId, handleNext }) => {
    const dispatch = useDispatch();
    const isMobile = useIsMobile();
    const [errorEnv, setErrorEnv] = useState(false);
    const [errorRecoPlantingDate, setErrorRecoPlantingDate] = useState(false);

    const disabled = useSelector(getBtnDisabled);
    const setDate = bindActionCreators(setPlantingDate, dispatch);
    const setDisable = bindActionCreators(setBtnDisabled, dispatch);
    const setSoilEnvironment = bindActionCreators(setEnvironment, dispatch);
    const setSoilHumidity = bindActionCreators(setHumidity, dispatch);
    const setSoilDensity = bindActionCreators(setDensity, dispatch);

    const plantingDate = useSelector(getPlantingDate);

    const units = useUnitContext();

    const { data: field, isValidating: fieldIsValidating } = useFieldById({ id: fieldId }, { enabled: !!fieldId });

    const environment = useSelector(getEnvironment);

    const recommendedEnvironmentandPlantingDateRange = useMemo(() => {
        if (!field) return [];
        const zones = field?.node.zones.filter((z) => z.type === 'corn') || [];

        const dateRanges: [dayjs.Dayjs, dayjs.Dayjs][] = (zones || []).map((zone) => {
            const assignedDates = assignYearsToDateRange(zone.minDate, zone.maxDate);
            const minDate = dayjs(assignedDates?.minDate) || dayjs();
            const maxDate = dayjs(assignedDates?.maxDate) || dayjs();

            return [minDate, maxDate];
        });

        dateRanges.sort((a, b) => a[0].isBefore(b[0]));

        const yearlessDateRanges = [-1, 0, 1].reduce((array, yearToAdd) => {
            return array.concat(
                dateRanges.map((range) => {
                    return range.map((date) => {
                        return parseDate(date.add(yearToAdd, 'year'));
                    });
                })
            );
        }, []);

        return yearlessDateRanges;
    }, [field]);

    const recommendationId = useSelector(getRecommendation);
    const { data: recommendation } = useRecommendationById(recommendationId);

    useEffect(() => {
        if (recommendation) {
            const recoPlantingDate = new Date(recommendation.crops?.[0]?.plantingDate);
            if (recoPlantingDate > new Date()) {
                setDate(undefined);
                setErrorRecoPlantingDate(true);
            } else {
                setDate(recommendation.crops?.[0]?.plantingDate);
            }

            setSoilHumidity(recommendation.crops?.[0]?.soilMoisture);
            setSoilEnvironment(recommendation.crops?.[0]?.environment);
            setSoilDensity(recommendation.crops?.[0]?.userDensity);
        }
    }, [recommendation]);

    useEffect(() => {
        if (
            [environment, plantingDate].filter((e) => {
                return e === undefined || e === '' || e === null;
            }).length ||
            !errorEnv
        ) {
            setDisable(true);
        } else {
            setDisable(false);
        }
    }, [environment, plantingDate, errorEnv]);

    useEffect(() => {
        if (validate(environment)) {
            setErrorEnv(true);
        } else {
            setErrorEnv(false);
        }
    }, [environment]);

    const validateForm = (e) => {
        if (!disabled && e.keyCode === 13) {
            handleNext();
        }
    };

    const validate = (value) => {
        const validation = { min: 1000, max: 15000 };
        return validation.min <= value && validation.max >= value;
    };

    const setEnv = (e) => {
        setSoilEnvironment(units.fromYield(e, 'corn'));
    };

    function parseDate(dateString: string) {
        return new Date(dateString).toISOString().split('T')[0];
    }

    return (
        <Grid container sx={{ pb: 2 }}>
            <Grid item xs={12}>
                {errorRecoPlantingDate && (
                    <Alert
                        severity='danger'
                        sx={{
                            my: 1,
                        }}
                    >
                        {_(
                            'La fecha de siembra proveniente de la recomendación ({{ date }}) corresponde a una fecha futura y por lo que no es posible utilizarla para calcular el estadio de tu cultivo.',
                            {
                                date: formatDate(new Date(recommendation?.crops?.[0]?.plantingDate)),
                            }
                        )}
                    </Alert>
                )}
                <Grid
                    container
                    spacing={!isMobile && 2}
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr 1fr',
                        ...(isMobile && {
                            gridTemplateColumns: '1fr',
                        }),
                    }}
                >
                    <Grid item>
                        <Grid sx={{ pt: 2 }} flexDirection='column' container spacing={2}>
                            <Grid item>
                                <Title bolder size='sm'>
                                    {`${_('Fecha de siembra')} *`}
                                </Title>
                            </Grid>
                            <Grid item>
                                <DateInput
                                    placeholder={_('Fecha de siembra')}
                                    onChange={(e) => setDate(e)}
                                    value={plantingDate}
                                    bgColor={Boolean(!errorRecoPlantingDate && recommendation && plantingDate)}
                                    app={settings.app.theme}
                                    color={!errorRecoPlantingDate && recommendation ? '' : 'gray-opacity'}
                                    validDates={recommendedEnvironmentandPlantingDateRange}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item>
                        <Grid container sx={{ pt: 2 }} flexDirection='column' spacing={2}>
                            <Grid item>
                                <Title bolder size='sm'>
                                    {_('Ambiente')} ({units.getUnit('yield', 'corn')}) *
                                </Title>
                            </Grid>
                            <Grid item>
                                <Input
                                    bgColor={Boolean(recommendation && environment)}
                                    type='number'
                                    color={settings.app.theme}
                                    placeholder={_('Ambiente')}
                                    onChange={(e) => setEnv(e)}
                                    value={units.toYield(environment, 'corn').value || ''}
                                    suffix={units.getUnit('yield', 'corn')}
                                    onKeyDown={(e) => validateForm(e)}
                                />
                            </Grid>
                            <Grid item>
                                <Title color={!environment ? 'gray-dark' : !errorEnv ? 'error' : 'green'} size='sm'>
                                    {_('Rango')} {units.toYield(1000, 'corn').value}-
                                    {units.toYield(15000, 'corn').value} {units.getUnit('yield', 'corn')}
                                </Title>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default EnvironmentStepper;
